import React, { useMemo, useState } from "react";
import { Typography } from "antd";
import { SbTable, SbTag, CaretDownOutlined } from "uiCore";
import { AGENT_STATUS_TAG_COLORS } from "constants/peoplePlusAI.constants";
import { groupBy } from "Utils/groupBy";
import {
  countUniqueRolesImpacted,
  buildFinancialColumns,
  buildPrdColumn,
  aggregateGroupTotals,
  formatVersions,
} from "./agentLibrary.utils";
import { ClickableRowTableWrapper, DescriptionText } from "./SbAgentLibraryV2.styles";

const { Text } = Typography;

// "View by Group" table: one row per group (from groupBy). Clicking a group row
// expands its agent sub-table; clicking an agent row opens that agent's PRD.
const SbAgentGroupTableV2 = ({ agents, loading, openPrd, canViewPotentialValue }) => {
  const [expandedKeys, setExpandedKeys] = useState([]);

  const groupRows = useMemo(() => {
    const grouped = groupBy(agents, "group_name");
    return Object.entries(grouped).map(([groupName, items]) => ({
      key: groupName || "Ungrouped",
      group_name: groupName || "Ungrouped",
      agents: items,
      agent_count: new Set(items.map((a) => a.agent_id)).size,
      roles_impacted: countUniqueRolesImpacted(items),
      ...aggregateGroupTotals(items),
    }));
  }, [agents]);

  const toggleGroup = (key) =>
    setExpandedKeys((keys) =>
      keys.includes(key) ? keys.filter((k) => k !== key) : [...keys, key],
    );

  const groupColumns = [
    {
      title: "Group",
      dataIndex: "group_name",
      render: (name) => <Text strong>{name}</Text>,
    },
    { title: "Agents", dataIndex: "agent_count", align: "center" },
    { title: "Roles Impacted", dataIndex: "roles_impacted", align: "center" },
    ...buildFinancialColumns(canViewPotentialValue),
    {
      title: "PRD",
      dataIndex: "prd_count",
      key: "prd_count",
      align: "center",
      render: (count) => count ?? 0,
    },
    {
      title: "Agents Description",
      key: "expand",
      align: "center",
      render: (_, record) => (
        <CaretDownOutlined rotate={expandedKeys.includes(record.key) ? 180 : 0} />
      ),
    },
  ];

  const agentColumns = [
    {
      title: "Agent",
      dataIndex: "agent_name",
      render: (name) => <Text strong>{name || "-"}</Text>,
    },
    {
      title: "Version",
      dataIndex: "version",
      key: "version",
      // The cell lists every selected version ("v1, v2"), so filter on each of them.
      filterValue: (record) =>
        record?.versions?.length ? record.versions : record?.version ?? null,
      filterLabel: (v) => `v${v}`,
      render: (_, record) => formatVersions(record),
    },
    {
      title: "Status",
      dataIndex: "status",
      render: (status) =>
        status ? (
          <SbTag color={AGENT_STATUS_TAG_COLORS[status] || "default"}>{status}</SbTag>
        ) : (
          "-"
        ),
    },
    {
      title: "Description",
      dataIndex: "description",
      width: 320,
      render: (text) =>
        text ? (
          <DescriptionText ellipsis={{ rows: 3, tooltip: text }}>{text}</DescriptionText>
        ) : (
          "-"
        ),
    },
    {
      title: "Roles Impacted",
      dataIndex: "roles_impacted",
      align: "center",
      render: (v) => v ?? 0,
    },
    ...buildFinancialColumns(canViewPotentialValue),
    // PRD cells show a "View" link or "—"; label the boolean filter Yes/No.
    { ...buildPrdColumn(openPrd), filterLabel: (ready) => (ready ? "Yes" : "No") },
  ];

  const expandedRowRender = (record) => (
      <SbTable
        columns={agentColumns}
        dataSource={record.agents}
        rowKey={(a) => `${a.project_id}:${a.agent_id}`}
        pagination={false}
        showHeader
        onRow={(agent) => ({ onClick: () => openPrd(agent) })}
      />
  );

  return (
    <ClickableRowTableWrapper>
      <SbTable
        columns={groupColumns}
        dataSource={groupRows}
        rowKey="key"
        loading={loading}
        sticky
        pagination={false}
        expandable={{
          expandedRowRender,
          expandedRowKeys: expandedKeys,
          showExpandColumn: false,
        }}
        onRow={(record) => ({ onClick: () => toggleGroup(record.key) })}
      />
    </ClickableRowTableWrapper>
  );
};

export default SbAgentGroupTableV2;
